import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';

const UploadFile = ({ inputId = 'upload-orden-file', acceptType, onFileLoaded, style }) => {
    const DEFAULT_MESSAGE = '';
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState(DEFAULT_MESSAGE);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const dragDepthRef = useRef(0);

    const handleFileRemove = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setFile(null);
        setFileName(DEFAULT_MESSAGE);
        onFileLoaded('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const validateAndLoad = (selectedFile) => {
        if (!selectedFile) {
            return handleFileRemove();
        }

        if (acceptType) {
            const allowed = acceptType.split(',').map((type) => type.trim().toLowerCase().replace('.', ''));
            const currentExtension = selectedFile.name.split('.').pop().toLowerCase();
            if (!allowed.includes(currentExtension)) {
                toast.warn('La extensión del archivo debe ser: ' + acceptType);
                return handleFileRemove();
            }
        }

        setFile(selectedFile);
        setFileName(selectedFile.name);
        onFileLoaded(selectedFile);
    };

    const handleFileChange = (e) => {
        validateAndLoad(e.target?.files[0]);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setDragOver(true);
        try {
            e.dataTransfer.dropEffect = 'copy';
        } catch {
            /* ignore */
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            e.dataTransfer.dropEffect = 'copy';
        } catch {
            /* ignore */
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setDragOver(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setDragOver(false);
        const dropped = e.dataTransfer?.files?.[0];
        validateAndLoad(dropped);
    };

    const hasFile = Boolean(file);

    return (
        <div className="upload-file-ordenes" style={{ ...style, minWidth: 0 }}>
            <div
                className="position-relative rounded-3 overflow-hidden"
                style={{
                    background: '#fff',
                    border: dragOver ? '2px dashed #c8102e' : '1px solid #e2e5e8',
                    boxShadow: dragOver
                        ? '0 0 0 3px rgba(200, 16, 46, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)'
                        : '0 1px 3px rgba(0, 0, 0, 0.05)',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {hasFile && (
                    <button
                        type="button"
                        className="btn btn-sm btn-light border-0 rounded-circle position-absolute shadow-sm"
                        style={{ top: 6, right: 6, width: 26, height: 26, padding: 0, zIndex: 2 }}
                        onClick={handleFileRemove}
                        title="Quitar archivo"
                        aria-label="Quitar archivo"
                    >
                        <i className="mdi mdi-close small text-secondary" />
                    </button>
                )}

                <label htmlFor={inputId} className="allow-pointer-cursor d-block mb-0">
                    <div className="px-2 py-3 text-center">
                        <div className="d-flex flex-column align-items-center gap-2">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: 40,
                                    height: 40,
                                    background: dragOver ? 'rgba(200, 16, 46, 0.08)' : '#f4f6f8',
                                    transition: 'background 0.2s ease',
                                }}
                            >
                                <i
                                    className={`mdi ${hasFile ? 'mdi-check-circle' : 'mdi-file-upload-outline'} color-rojo-megatone`}
                                    style={{ fontSize: '1.35rem', lineHeight: 1 }}
                                />
                            </span>

                            <div className="w-100 px-1">
                                <div className="small fw-semibold text-dark lh-sm">Comprobante PDF</div>
                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                    {dragOver ? 'Soltá el archivo aquí' : 'Arrastrá el archivo o tocá para buscar'}
                                </div>
                            </div>

                            {hasFile && fileName && (
                                <div className="d-flex align-items-center gap-1 w-100 small text-success px-1 min-w-0">
                                    <i className="mdi mdi-file-pdf-box flex-shrink-0" aria-hidden />
                                    <span className="text-truncate min-w-0" title={fileName}>
                                        {fileName}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <input
                        type="file"
                        id={inputId}
                        accept={acceptType}
                        className="d-none"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />
                </label>
            </div>
        </div>
    );
};

export default UploadFile;
